/**
 * API Service
 * All requests go through api-fl-polaris which handles load balancing and failover
 */

// Single API endpoint - load balancer handles failover
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api-fl-polaris.polysansoft.com';

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  headers?: Record<string, string>;
  body?: unknown;
  timeout?: number;
}

interface ApiResponse<T> {
  data: T | null;
  error: string | null;
  status: number;
}

/**
 * Fetch with timeout support
 */
async function fetchWithTimeout(
  url: string,
  options: RequestInit,
  timeout: number = 10000
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
}

/**
 * Main API request function
 */
export async function apiRequest<T>(
  path: string,
  options: ApiOptions = {}
): Promise<ApiResponse<T>> {
  const url = `${API_BASE_URL}${path}`;

  const fetchOptions: RequestInit = {
    method: options.method || 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include', // Send cookies for sticky session support
  };

  if (options.body && options.method !== 'GET') {
    fetchOptions.body = JSON.stringify(options.body);
  }

  try {
    const response = await fetchWithTimeout(url, fetchOptions, options.timeout || 10000);

    // Handle response
    if (response.ok) {
      const data = await response.json();
      return { data, error: null, status: response.status };
    }

    // Client/Server errors - parse error message
    const errorData = await response.json().catch(() => ({ detail: `Error: ${response.status}` }));
    return {
      data: null,
      error: errorData.detail || errorData.error || `Error: ${response.status}`,
      status: response.status,
    };
  } catch (error) {
    // Network error or timeout
    console.error('[API] Request failed:', error);
    return {
      data: null,
      error: 'Network error. Please check your connection and try again.',
      status: 0,
    };
  }
}

/**
 * Convenience methods
 */
export const api = {
  get: <T>(path: string, token?: string) =>
    apiRequest<T>(path, {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    }),

  post: <T>(path: string, body: unknown, token?: string) =>
    apiRequest<T>(path, {
      method: 'POST',
      body,
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    }),

  put: <T>(path: string, body: unknown, token?: string) =>
    apiRequest<T>(path, {
      method: 'PUT',
      body,
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    }),

  delete: <T>(path: string, token?: string) =>
    apiRequest<T>(path, {
      method: 'DELETE',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    }),
};

// User data type for auth responses
type UserData = { id: string; email: string; name: string; membershipTier?: number; membershipValidTill?: string; isExpired?: boolean };

/**
 * Auth-specific API calls (BackendMaster endpoints)
 */
// 2FA response types
export interface TwoFARequiredResponse {
  require_2fa: boolean;
  temp_token: string;
}

export interface TwoFASetupResponse {
  secret: string;
  qr_uri: string;
  backup_codes: string[];
}

export interface TwoFAVerifySetupResponse {
  message: string;
  backup_codes: string[];
}

export interface TwoFAStatusResponse {
  enabled: boolean;
  backup_codes_remaining: number;
}

// Login can return either user+token OR 2FA required response
type LoginResponse = { user: UserData; token: string } | TwoFARequiredResponse;

// Type guard to check if 2FA is required
export function is2FARequired(response: LoginResponse): response is TwoFARequiredResponse {
  return 'require_2fa' in response && response.require_2fa === true;
}

export const authApi = {
  login: (email: string, password: string) =>
    api.post<LoginResponse>(
      '/BackendMaster/login',
      { email, password }
    ),

  // Verify 2FA code after password login
  verify2FA: (tempToken: string, code: string) =>
    api.post<{ user: UserData; token: string }>(
      '/BackendMaster/login/verify-2fa',
      { temp_token: tempToken, code }
    ),

  // Register now returns a message (verification email sent)
  register: (name: string, email: string, password: string) =>
    api.post<{ message: string }>(
      '/BackendMaster/register',
      { name, email, password }
    ),

  // Verify email token to complete registration
  verifyEmail: (token: string) =>
    api.post<{ user: UserData; token: string }>(
      '/BackendMaster/verify-email',
      { token }
    ),

  // Request password reset email
  forgotPassword: (email: string) =>
    api.post<{ message: string }>(
      '/BackendMaster/forgot-password',
      { email }
    ),

  // Reset password with token
  resetPassword: (token: string, password: string) =>
    api.post<{ message: string }>(
      '/BackendMaster/reset-password',
      { token, password }
    ),

  logout: (_token: string) => Promise.resolve({ data: { success: true }, error: null, status: 200 }),

  me: (token: string) => api.get<{ user: UserData }>('/BackendMaster/me', token),
};

/**
 * 2FA API calls (BackendMaster endpoints)
 */
export const twoFactorApi = {
  // Get 2FA status
  getStatus: (token: string) =>
    api.get<TwoFAStatusResponse>('/BackendMaster/2fa/status', token),

  // Initialize 2FA setup (returns secret, QR URI, and backup codes)
  setup: (token: string) =>
    api.post<TwoFASetupResponse>('/BackendMaster/2fa/setup', {}, token),

  // Verify TOTP code to enable 2FA
  verifySetup: (token: string, code: string) =>
    api.post<TwoFAVerifySetupResponse>('/BackendMaster/2fa/verify-setup', { code }, token),

  // Disable 2FA (requires password and TOTP/backup code)
  disable: (token: string, password: string, code: string) =>
    api.post<{ message: string }>('/BackendMaster/2fa/disable', { password, code }, token),

  // Regenerate backup codes (requires TOTP code)
  regenerateBackupCodes: (token: string, code: string) =>
    api.post<TwoFAVerifySetupResponse>('/BackendMaster/2fa/regenerate-backup-codes', { code }, token),
};

/**
 * Platform Contacts types
 */
export interface PlatformContactInfo {
  email: string | null;
  email_verified: boolean;
  phone: string | null;
  is_default: boolean;  // True if email is user's main email (not stored)
}

export interface PlatformContactsResponse {
  contacts: Record<string, PlatformContactInfo>;
}

export type PlatformName = 'trendyol' | 'hepsiburada' | 'n11' | 'amazon';

export const PLATFORM_DISPLAY_NAMES: Record<PlatformName, string> = {
  trendyol: 'Trendyol',
  hepsiburada: 'Hepsiburada',
  n11: 'N11',
  amazon: 'Amazon',
};

/**
 * Platform Contacts API calls (BackendMaster endpoints)
 */
export const platformContactsApi = {
  // Get all platform contacts
  getContacts: (token: string) =>
    api.get<PlatformContactsResponse>('/BackendMaster/platform-contacts', token),

  // Update platform contact (phone only, or clear email)
  updateContact: (token: string, platform: PlatformName, data: { email?: string; phone?: string }) =>
    api.post<{ message: string }>('/BackendMaster/platform-contacts/update', { platform, ...data }, token),

  // Send verification code to email
  sendVerification: (token: string, platform: PlatformName, email: string) =>
    api.post<{ message: string; expires_in_minutes: number }>(
      '/BackendMaster/platform-contacts/send-verification',
      { platform, email },
      token
    ),

  // Verify email with code
  verifyEmail: (token: string, platform: PlatformName, code: string) =>
    api.post<{ message: string }>('/BackendMaster/platform-contacts/verify-email', { platform, code }, token),
};

/**
 * Dashboard API calls
 */
export const dashboardApi = {
  getData: (token: string) =>
    api.get<{
      products: Array<{
        id: string;
        status: string;
        name?: string;
        sku?: string;
        price?: number;
        stock?: number;
        deactivationReason?: string;
        [key: string]: unknown;
      }>;
      apiKeys: Array<{
        id: string;
        versionId: string;
        key: string;
        createdAt: string;
      }>;
    }>('/api/dashboard', token),
};

/**
 * API Keys management (ApiMaster endpoints)
 */
export interface ApiKeyFromServer {
  id: string;
  platform: string;
  keyType: string;
  sellerId: string | null;
  expiresAt: string | null;
  isActive: boolean;
}

export const apiKeysApi = {
  getAll: (token: string) =>
    api.get<{
      apiKeys: ApiKeyFromServer[];
    }>('/ApiMaster/keys', token),

  delete: (id: string, token: string) =>
    api.delete<{ success: boolean; message: string }>(`/ApiMaster/keys/${id}`, token),
};

/**
 * Befel (Order/Command) from server - BefelManagerMaster endpoints
 */
export interface BefelFromServer {
  id: string;
  platform: string;
  urun: string;
  sku: string;  // Comma-separated barcodes for variants
  alt: number;
  ust: number;
  paraTipi: number;
  emirTipi: number;
  active: boolean;
  workerID: number;
  deaktivasyonNedeni: number | null;
  url: string | null;
  modelKodu: string | null;  // Model code for variant grouping
  priceIncludesCommission: boolean;
  commissionPct: number | null;
  thumbnail: string | null;  // Product thumbnail URL
  activateCoupon: boolean;  // Enable automatic coupon management
  activatePromotion: boolean;  // Enable automatic promotion management
}

export const befelsApi = {
  getAll: (token: string, includeInactive: boolean = true) =>
    api.get<{ befels: BefelFromServer[]; current_count: number; max_count: number }>(
      `/BefelManagerMaster/befels?include_inactive=${includeInactive}`,
      token
    ),

  create: (
    token: string,
    data: {
      platform: string;
      urun: string;
      sku: string;  // This is the barcode
      alt: number;
      ust: number;
      paraTipi: number;  // Currency type (0=TRY, 1=USD, 2=EUR)
      emirTipi: number;
      url?: string;
      modelKodu?: string;
      priceIncludesCommission?: boolean;
      activateCoupon?: boolean;
      activatePromotion?: boolean;
    }
  ) => api.post<BefelFromServer>('/BefelManagerMaster/befels', data, token),

  update: (
    id: string,
    token: string,
    data: { alt?: number; ust?: number; paraTipi?: number; active?: boolean; emirTipi?: number; priceIncludesCommission?: boolean; activateCoupon?: boolean; activatePromotion?: boolean }
  ) => api.put<BefelFromServer>(`/BefelManagerMaster/befels/${id}`, data, token),

  delete: (id: string, token: string) =>
    api.delete<{ success: boolean; message: string }>(`/BefelManagerMaster/befels/${id}`, token),

  restart: (id: string, token: string) =>
    api.post<BefelFromServer>(`/BefelManagerMaster/befels/${id}/restart`, {}, token),

  getStats: (token: string) =>
    api.get<{ total: number; unclaimed: number; claimed: number; inactive: number }>(
      '/BefelManagerMaster/stats',
      token
    ),

  /**
   * Bulk create befels from Excel upload
   * @param token - Auth token
   * @param platform - Platform name (e.g., 'TRENDYOL')
   * @param orders - Array of order items
   */
  bulkCreate: (
    token: string,
    platform: string,
    orders: Array<{
      barkod: string;
      altFiyat: number;
      ustFiyat: number;
      paraBirimi: number;
      emirTipi: number;
      priceIncludesCommission: boolean;
      activateCoupon: boolean;
      activatePromotion: boolean;
    }>
  ) =>
    apiRequest<{
      results: Array<{
        row: number;
        barkod: string;
        success: boolean;
        error?: string;
        befelId?: string;
        action?: 'CREATED' | 'UPDATED' | 'UNCHANGED';
        note?: string;
      }>;
      successCount: number;
      errorCount: number;
      createdCount: number;
      updatedCount: number;
      unchangedCount: number;
    }>('/BefelManagerMaster/befels/bulk', {
      method: 'POST',
      body: { platform: platform.toUpperCase(), orders },
      headers: { Authorization: `Bearer ${token}` },
      timeout: 120000,  // 2 minutes for bulk operations
    }),
};

/**
 * Platform Products API (BackendMaster endpoints)
 * Fetches products from connected platforms using user's API keys
 */
export interface PlatformProductFromServer {
  id: string;
  platform: string;
  name: string;
  sku: string;
  barcode?: string;
  modelKodu?: string;
  currentPrice: number;
  currency: number; // 0=TRY, 1=USD, 2=EUR, 3=CNY
  stock: number;
  url?: string;
  attributes?: Record<string, string>;  // Key-value pairs from product attributes
  images?: string[];  // List of image URLs
  categoryName?: string;  // Product category
  brandName?: string;  // Product brand
  // Listing-level status flags (HB only, from listing API)
  // Future: use isSalable=true, isLocked=false query params to pre-filter on backend
  isSalable?: boolean | null;
  isLocked?: boolean | null;
  isSuspended?: boolean | null;
  isFrozen?: boolean | null;
}

export interface ProductsResponse {
  products: PlatformProductFromServer[];
  page: number;
  totalPages: number;
  totalProducts: number;
}

export interface AllProductsResponse {
  products: PlatformProductFromServer[];
  totalProducts: number;
  categories: string[];  // Unique category names for filtering
  brands: string[];  // Unique brand names for filtering
}

export const productsApi = {
  /**
   * Get products from a platform with pagination
   * @param token - Auth token
   * @param platform - Platform name (e.g., 'TRENDYOL')
   * @param page - Page number (1-based)
   * @param limit - Products per page (max 200)
   */
  getAll: (token: string, platform: string, page: number = 1, limit: number = 200) => {
    const params = new URLSearchParams({
      platform: platform.toUpperCase(),
      page: page.toString(),
      limit: Math.min(limit, 200).toString(),
    });
    return api.get<ProductsResponse>(
      `/BackendMaster/products?${params}`,
      token
    );
  },

  /**
   * Get ALL products from a platform (fetches all pages at once)
   * Backend fetches all pages and returns everything.
   * Frontend handles filtering and pagination locally.
   * @param token - Auth token
   * @param platform - Platform name (e.g., 'TRENDYOL')
   */
  getAllProducts: (token: string, platform: string) => {
    const params = new URLSearchParams({
      platform: platform.toUpperCase(),
    });
    return apiRequest<AllProductsResponse>(
      `/BackendMaster/products/all?${params}`,
      {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000,  // 2 minutes for fetching all products
      }
    );
  },
};

/**
 * Platform definitions (frontend-only, no API call needed)
 * Trendyol is active, others are coming soon
 */
export const PLATFORMS = [
  { id: 'TRENDYOL', name: 'Trendyol', active: true },
  { id: 'HEPSIBURADA', name: 'Hepsiburada', active: true },
  { id: 'N11', name: 'N11', active: false, comingSoon: true },
  { id: 'AMAZONTR', name: 'Amazon TR', active: false, comingSoon: true },
] as const;

export type PlatformId = typeof PLATFORMS[number]['id'];

/**
 * Payment API calls (BackendMaster endpoints)
 * For Garanti BBVA Sanal POS 3D_PAY integration
 */
export interface PaymentInitResponse {
  form_data: Record<string, string>;
  gateway_url: string;
  merchant_oid: string;
}

export interface BillingInfo {
  invoice_type: 'individual' | 'corporate' | 'end_consumer';
  billing_name: string;
  billing_id_no: string;
  billing_address: string;
  billing_tax_office: string;
}

export const paymentApi = {
  /**
   * Initialize a payment session
   * Returns form data to be submitted to Garanti 3D Secure gateway
   * Card details are collected on frontend and added to the form
   * @param token - Auth token
   * @param tierId - Membership tier (1-10)
   * @param period - 'monthly' or 'annual'
   * @param billing - Billing/invoice information
   */
  init: (token: string, tierId: number, period: 'monthly' | 'annual', billing: BillingInfo) =>
    api.post<PaymentInitResponse>(
      '/BackendMaster/payment/init',
      {
        tier_id: tierId,
        period,
        ...billing
      },
      token
    ),
};

/**
 * Get current API endpoint (for debugging/status display)
 */
export function getApiStatus() {
  return {
    currentEndpoint: API_BASE_URL,
  };
}
